<template>
  <modal :show.sync="show" :on-close="onClose">
    <div class="column content-box">
      <woot-modal-header :header-title="pageTitle" />
      <form class="row medium-8" @submit.prevent="editAgent()">
        <div class="medium-12 columns">
          <input
            v-model.trim="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            maxlength="50"
            @input="$v.agentName.$touch"
          />
        </div>

        <div
          class="medium-12 columns"
          style="margin-bottom: 1.5rem!important; margin-top: 0.5rem!important;"
        >
          <multiselect-dropdown
            ref="dropdownComponent0"
            :options="mergedRoles"
            :selected-item="selectedRole"
            :multiselector-title="
              $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.PLACEHOLDER')
            "
            :multiselector-placeholder="
              $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.PLACEHOLDER')
            "
            :add-agent="true"
            :has-thumbnail="false"
            :close="closeDropdown0"
            @click="selectRole"
            @close="onChangeStatus0"
          />
          <span v-if="$v.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </div>

        <div class="medium-12 columns">
          <label
            :class="{ error: $v.agentAvailability.$error }"
            style="display: block;"
          >
            {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
            <multiselect-dropdown
              ref="dropdownComponent1"
              :options="availabilityStatuses"
              :selected-item="agentAvailability"
              :multiselector-title="
                $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.PLACEHOLDER')
              "
              :multiselector-placeholder="
                $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.PLACEHOLDER')
              "
              :edit-agent="true"
              :has-thumbnail="false"
              :close="closeDropdown1"
              @click="selectAvailability"
              @close="onChangeStatus1"
            />
            <span v-if="$v.agentAvailability.$error" class="message">
              {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
            </span>
          </label>
        </div>
        <div
          class="medium-12 modal-footer"
          style="margin-top: 2rem; margin-bottom: -3rem "
        >
          <div class="medium-6 columns" style="margin-right: 0.6rem;">
            <button class="button clear" @click.prevent="onClose">
              {{ $t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT') }}
            </button>
            <woot-submit-button
              :disabled="
                $v.selectedRoleId.$invalid ||
                  $v.agentName.$invalid ||
                  uiFlags.isUpdating
              "
              :button-text="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
              :loading="uiFlags.isUpdating"
            />
          </div>
          <div class="medium-6 columns text-right">
            <woot-button
              icon="lock-closed"
              variant="clear"
              @click.prevent="resetPassword"
            >
              {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON') }}
            </woot-button>
          </div>
        </div>
      </form>
    </div>
  </modal>
</template>

<script>
import { required, minLength } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import WootSubmitButton from '../../../../components/buttons/FormSubmitButton';
import Modal from '../../../../components/Modal';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

export default {
  components: {
    WootSubmitButton,
    Modal,
    MultiselectDropdown,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    email: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    customRoleId: {
      type: [Number, String],
      default: null,
    },
    availability: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      // selectedRoleId stores the selected role identifier (string for defaults, number for customs)
      selectedRoleId: this.customRoleId
        ? String(this.customRoleId)
        : this.type || 'agent',
      agentName: this.name,
      agentAvailability: {
        id: 1,
        label: '',
        value: this.availability,
      },
      // keep legacy agentType for compatibility where needed, but primary selection is selectedRoleId
      agentType: {
        id: this.type === 'administrator' ? 1 : 2,
        name: this.type === 'administrator' ? 'Administrator' : 'Agent',
        type: this.type,
      },
      agentCredentials: {
        email: this.email,
      },
      show: true,
      // close0 and dropdownComponent0 used by multiselect dropdown
      close0: false,
      close1: false,
    };
  },
  validations: {
    agentName: {
      required,
      minLength: minLength(1),
    },
    selectedRoleId: { required },
    agentAvailability: {
      required,
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('AGENT_MGMT.EDIT.TITLE')} - ${this.name}`;
    },
    ...mapGetters({
      uiFlags: 'agents/getUIFlags',
      customRoles: 'customRoles/getCustomRoles',
    }),
    // merge default roles with custom roles from store
    mergedRoles() {
      const defaultRoles = [
        {
          id: 'administrator',
          type: 'administrator',
          name: 'administrator',
          label: this.$t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
        },
        {
          id: 'agent',
          type: 'agent',
          name: 'agent',
          label: this.$t('AGENT_MGMT.AGENT_TYPES.AGENT'),
        },
      ];

      const custom = (this.customRoles || []).map(r => ({
        id: `custom_${r.id}`,
        type: 'custom',
        name: r.name,
        label: r.name,
      }));

      return [...defaultRoles, ...custom];
    },
    selectedRole() {
      return this.mergedRoles.find(
        role =>
          String(role.id) === String(this.selectedRoleId) ||
          role.id === this.selectedRoleId
      );
    },
    availabilityStatuses() {
      return this.$t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUSES_LIST').map(
        (statusLabel, index) => ({
          label: statusLabel,
          value: AVAILABILITY_STATUS_KEYS[index],
          disabled:
            this.currentUserAvailability === AVAILABILITY_STATUS_KEYS[index],
        })
      );
    },
    closeDropdown0() {
      return this.close0;
    },
    closeDropdown1() {
      return this.close1;
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.handleClickOutside0);
    document.addEventListener('mousedown', this.handleClickOutside1);
    // initialize selectedRoleId properly: if customRoleId exists, use it; otherwise use the type (default role)
    if (this.customRoleId) {
      this.selectedRoleId = `custom_${String(this.customRoleId)}`;
    } else {
      this.selectedRoleId = this.type || 'agent';
    }
    // also sync agentType for multiselect compatibility
    if (this.selectedRole) {
      this.agentType = this.selectedRole;
    }
    // Initialize availability label from i18n now that Vue is ready
    const statuses = this.$t(
      'PROFILE_SETTINGS.FORM.AVAILABILITY.STATUSES_LIST'
    );
    if (this.availability === 'online') {
      this.agentAvailability.label = statuses[0];
      this.agentAvailability.id = 1;
    } else if (this.availability === 'busy') {
      this.agentAvailability.label = statuses[1];
      this.agentAvailability.id = 2;
    } else {
      this.agentAvailability.label = statuses[2];
      this.agentAvailability.id = 3;
    }
  },
  methods: {
    showAlert(message) {
      bus.$emit('newToastMessage', message);
    },
    // when the multiselect emits a selected role, keep legacy agentType and update selectedRoleId
    selectRole(role) {
      // keep agentType for compatibility
      this.agentType = role || this.agentType;
      // selectedRoleId is stored as string to unify with default role ids
      if (role && typeof role.id !== 'undefined') {
        this.selectedRoleId = String(role.id);
      } else if (role && role.name) {
        this.selectedRoleId = role.name;
      }
      // mark validation touched
      if (this.$v && this.$v.selectedRoleId) this.$v.selectedRoleId.$touch();
    },
    selectAvailability(status) {
      const getAvailabilityId = value => {
        if (value === 'online') return 1;
        if (value === 'busy') return 2;
        return 3;
      };

      const getAvailabilityLabel = value => {
        const statuses = this.$t(
          'PROFILE_SETTINGS.FORM.AVAILABILITY.STATUSES_LIST'
        );
        if (value === 'online') return statuses[0];
        if (value === 'busy') return statuses[1];
        return statuses[2];
      };

      if (
        this.agentAvailability &&
        this.agentAvailability.value === status.value
      ) {
        this.agentAvailability = {
          id: getAvailabilityId(this.availability),
          label: getAvailabilityLabel(this.availability),
          value: this.availability,
        };
      } else {
        this.agentAvailability = {
          id: getAvailabilityId(status.value),
          label: status.label,
          value: status.value,
        };
      }
    },
    async editAgent() {
      try {
        const payload = {
          id: this.id,
          name: this.agentName,
          availability: this.agentAvailability.value,
        };

        // If a custom role is selected, assign the agent to the 'agent' role and set custom_role_id.
        // If a default role is selected, send role and ensure custom_role_id is null.
        if (
          this.selectedRole &&
          String(this.selectedRole.id).startsWith('custom_')
        ) {
          // first remove 'custom_' prefix to get numeric id
          payload.custom_role_id = Number(
            this.selectedRole.id.replace('custom_', '')
          );
          payload.role = 'agent';
        } else if (this.selectedRole) {
          payload.role = this.selectedRole.id;
          payload.custom_role_id = null;
        }

        await this.$store.dispatch('agents/update', payload);
        this.showAlert(this.$t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        this.showAlert(this.$t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async resetPassword() {
      try {
        await Auth.resetPassword(this.agentCredentials);
        this.showAlert(
          this.$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE')
        );
      } catch (error) {
        this.showAlert(this.$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
      }
    },
    handleClickOutside0(event) {
      // Check that the reference exists before accessing it
      if (
        this.$refs.dropdownComponent0 &&
        this.$refs.dropdownComponent0.$refs.dropdownElement
      ) {
        const dropdownElement = this.$refs.dropdownComponent0.$refs
          .dropdownElement;
        if (!dropdownElement.contains(event.target)) {
          this.onCloseDropdown0();
        }
      }
    },
    onCloseDropdown0() {
      this.close0 = true;
    },
    onChangeStatus0(value) {
      if (value) {
        this.close0 = false;
      }
    },
    handleClickOutside1(event) {
      // Check that the reference exists before accessing it
      if (
        this.$refs.dropdownComponent1 &&
        this.$refs.dropdownComponent1.$refs.dropdownElement
      ) {
        const dropdownElement = this.$refs.dropdownComponent1.$refs
          .dropdownElement;
        if (!dropdownElement.contains(event.target)) {
          this.onCloseDropdown1();
        }
      }
    },
    onCloseDropdown1() {
      this.close1 = true;
    },
    onChangeStatus1(value) {
      if (value) {
        this.close1 = false;
      }
    },
  },
};
</script>
